import React, { useCallback, useEffect, useState } from "react";
import { Drawer, Box, Typography, IconButton, CircularProgress, Button, Stack, Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions, Chip, Table, TableBody, TableCell, TableRow, Alert } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import useAxiosRequests from "../../../hooks/useAxiosRequest";
import ExternalRoutes from "../../../config/ExternalRoutes";
import { API_BASE_URL } from "../../../config/BaseUrl";
import pdfImage from "../../../img/pdf.png";
import DocumentUpdate from "./DocumentUpdate";

const ShowDocument = ({ open, handleClose, docName, onUpdated }) => {
  const { sendRequest } = useAxiosRequests();
  const [loading, setLoading] = useState(false);
  const [doc, setDoc] = useState({});
  const [openUpdate, setOpenUpdate] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);

  const fetchDocumentDetails = useCallback(async () => {
    setLoading(true);
    try {
      const response = await sendRequest({
        method: "GET",
        url: ExternalRoutes.document.detail + "?doc=" + encodeURIComponent(docName),
      });
      if (response.status === "success") {
        setDoc(response.data || null);
      } else {
        setDoc('');
      }
    } catch (error) {
      console.error("Error fetching document details:", error);
      setDoc(null);
    } finally {
      setLoading(false);
    }
  }, [docName]);

  useEffect(() => {
    if (open) {
      fetchDocumentDetails();
    }
    return () => {
      setDoc(null);
      setLoading(false);
    };
  }, [open, fetchDocumentDetails]);

  const handleDelete = async () => {
    try {
      await sendRequest({
        method: "DELETE",
        url: ExternalRoutes.document.conDocuments + "/" + doc.id,
      });
      setOpenDeleteDialog(false);
      handleClose();
      if (onUpdated) onUpdated();
    } catch (error) {
      setOpenDeleteDialog(false);
    }
  };

  function formatDocForUpdate(doc) {
    if (!doc) return undefined;
    if (typeof doc.docFile === "object" && doc.docFile !== null) {
      return { row: doc };
    }
    const filePath = doc.docFile || "";
    const extension = filePath.split('.').pop()?.toLowerCase();
    return {
      row: {
        ...doc,
        docFile: {
          flName: filePath.split('/').pop(),
          flUrl: filePath,
          flExtension: extension,
        }
      }
    };
  }

  return (
    <>
      <Drawer anchor="right" open={open} onClose={handleClose}>
        <Box sx={{ width: 600, p: 3, position: "relative" }}>
          <IconButton
            onClick={handleClose}
            sx={{ position: "absolute", right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
          <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
            <Typography variant="h6" sx={{ mr: 1 }}>
              Document :
            </Typography>
            <Typography variant="h6" sx={{ fontWeight: "bold" }}>
              {docName}
            </Typography>
          </Box>

          {loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
              <CircularProgress />
            </Box>
          ) : doc ? (
            <>
              <Table sx={{ mb: 3 }}>
                <TableBody>
                  <TableRow>
                    <TableCell sx={{ fontWeight: "bold", width: 120 }}>Propriétaire</TableCell>
                    <TableCell>{doc.userName}</TableCell>
                    <TableCell sx={{ fontWeight: "bold", width: 120 }}>Date</TableCell>
                    <TableCell>{doc.date}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell sx={{ fontWeight: "bold" }}>Statut</TableCell>
                    <TableCell>{doc.status_name}</TableCell>
                    <TableCell>
                      {doc.isSubmit && (
                        <Chip label="Soumis" color="warning" size="small" />
                      )}
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
              <Typography variant="subtitle1" sx={{ fontWeight: "bold" }}>
                Fichier :
              </Typography>
              {doc.docFile && (
                <>
                  {/\.(pdf)$/i.test(doc.docFile) ? (
                    <Box sx={{ mt: 2 }}>
                      <a href={`${API_BASE_URL}${doc.docFile}`} target="_blank" rel="noopener noreferrer">
                        <img
                          src={pdfImage}
                          alt="aperçu pdf"
                          style={{ maxWidth: "100%", maxHeight: "100%", borderRadius: 8 }}
                        />
                        <Typography sx={{ mt: 1 }}>Télécharger / Voir le PDF</Typography>
                      </a>
                    </Box>
                  ) : (
                    <Box sx={{ mt: 2 }}>
                      <img
                        src={`${API_BASE_URL}${doc.docFile}`}
                        alt={doc.name}
                        style={{ maxWidth: "100%", maxHeight: "100%", borderRadius: 8 }}
                      />
                    </Box>
                  )}
                </>
              )}

              {/* Boutons Modifier et Supprimer */}
              <Stack direction="row" spacing={2} sx={{ mt: 4 }}>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={() => setOpenUpdate(true)}
                  disabled={!doc}
                >
                  Modifier
                </Button>
                <Button
                  variant="outlined"
                  color="error"
                  onClick={() => setOpenDeleteDialog(true)}
                  disabled={!doc}
                >
                  Supprimer
                </Button>
              </Stack>
            </>
          ) : (
            <Alert severity="error" sx={{ mt: 4 }}>
              Le document demandé n'existe pas ou a été supprimé.<br />
              Vérifiez le lien ou contactez un administrateur si besoin.
            </Alert>
          )}
        </Box>
      </Drawer>

      {/* Drawer de modification */}
      <DocumentUpdate
        open={openUpdate}
        handleClose={() => setOpenUpdate(false)}
        documentData={formatDocForUpdate(doc)}
        onUpdated={() => {
          setOpenUpdate(false);
          fetchDocumentDetails();
          if (onUpdated) onUpdated();
        }}
      />

      {/* Dialog de confirmation suppression */}
      <Dialog open={openDeleteDialog} onClose={() => setOpenDeleteDialog(false)}>
        <DialogTitle>Supprimer le document</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Êtes-vous sûr de vouloir supprimer ce document ? Cette action est irréversible.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDeleteDialog(false)} color="inherit">
            Annuler
          </Button>
          <Button onClick={handleDelete} color="error" variant="contained">
            Confirmer
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ShowDocument;